import React, { useEffect, useState } from 'react';
import { Sparkles, Check, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { paymentsAPI, apiErrorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export default function BuyCreditsModal({ open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const [packs, setPacks] = useState([]);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    if (open && packs.length === 0) {
      paymentsAPI.getPacks().then((res) => setPacks(res.data.packs || [])).catch(() => {});
    }
  }, [open, packs.length]);

  const handleBuy = async (pack) => {
    setPaying(pack.id);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error('Could not load payment gateway. Check your connection.');
        return;
      }
      const { data } = await paymentsAPI.createOrder(pack.id);
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: 'XceedAgents.ai',
        description: `${pack.credits} workspace credits`,
        prefill: { name: user?.name, email: user?.email },
        theme: { color: '#EA580C' },
        handler: async (response) => {
          try {
            const verify = await paymentsAPI.verify({ ...response, pack_id: pack.id });
            toast.success(`${verify.data.credits_added} credits added! Balance: ${verify.data.credits_balance}`);
            await refreshUser();
            onOpenChange(false);
          } catch (e) {
            toast.error(apiErrorMessage(e, 'Payment verification failed. Contact support.'));
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not start payment'));
    } finally {
      setPaying(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="buy-credits-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Buy Workspace Credits</DialogTitle>
          <DialogDescription>
            Credits power every stage — prototypes, full development builds, AI QA and release packaging.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mt-2 max-h-[60vh] overflow-y-auto pr-1">
          {packs.map((pack) => (
            <div
              key={pack.id}
              className="relative rounded-xl border p-5 flex flex-col"
              style={{
                borderColor: pack.popular ? 'var(--primary-accent)' : 'var(--border)',
                background: 'var(--card-bg)',
              }}
              data-testid={`credit-pack-${pack.id}`}
            >
              {pack.popular && (
                <span
                  className="absolute -top-2.5 left-4 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
                  style={{ background: 'var(--primary-accent)' }}
                >
                  Most popular
                </span>
              )}
              <p className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>{pack.name}</p>
              <div className="flex items-center gap-1.5 mt-2">
                <Sparkles className="w-5 h-5" style={{ color: 'var(--primary-accent)' }} />
                <span className="font-heading text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {pack.credits}
                </span>
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>credits</span>
              </div>
              <p className="text-xs mt-2 flex-1" style={{ color: 'var(--text-secondary)' }}>{pack.blurb}</p>
              <p className="font-bold text-lg mt-3" style={{ color: 'var(--text-primary)' }}>
                ₹{pack.amount_inr.toLocaleString('en-IN')}
                <span className="text-xs font-medium ml-1.5" style={{ color: 'var(--text-muted)' }}>≈ ${pack.amount_usd}</span>
              </p>
              <Button
                className="mt-3 w-full font-semibold text-white"
                style={{ background: 'var(--primary-accent)' }}
                disabled={paying === pack.id}
                onClick={() => handleBuy(pack)}
                data-testid={`buy-pack-${pack.id}-btn`}
              >
                {paying === pack.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1.5" /> Buy now</>}
              </Button>
            </div>
          ))}
          {packs.length === 0 && (
            <div className="col-span-3 flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary-accent)' }} />
            </div>
          )}
        </div>
        <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
          Secure payment via Razorpay (UPI, cards, netbanking). Credits never expire.
        </p>
      </DialogContent>
    </Dialog>
  );
}
